import type { ChangeEvent, KeyboardEvent } from "react";
import { useEffect, useRef, useState } from "react";

import type { IconName } from "@similarweb/icons";

import { StyledText, StyledInputContainer, StyledInput, StyledSubmitButton } from "./styles";

type EditableTextProps = {
    text: string;
    isBeingEdited: boolean;
    className?: string;
    maxInputWidth?: number;
    iconName?: IconName;
    onCancel(): void;
    onSubmit(editedValue: string): void;
    isTextValid?(text: string): boolean;
};

const EditableText = (props: EditableTextProps) => {
    const {
        text,
        isBeingEdited,
        className = null,
        maxInputWidth,
        onCancel,
        onSubmit,
        isTextValid = (t: string) => t.trim().length > 3,
        iconName = "checked",
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const [textBeingEdited, setTextBeingEdited] = useState(text);

    useEffect(() => {
        if (textBeingEdited !== text) {
            setTextBeingEdited(text);
        }
    }, [text]);

    useEffect(() => {
        if (!isBeingEdited && text !== textBeingEdited) {
            return setTextBeingEdited(text);
        }

        if (isBeingEdited) {
            inputRef.current?.select();
        }
    }, [isBeingEdited]);

    function handleSubmit(e: KeyboardEvent<HTMLInputElement>) {
        e.stopPropagation();
        if (isTextValid(textBeingEdited)) {
            onSubmit(textBeingEdited.trim());
        }
    }

    function handleKeyUp(e: KeyboardEvent<HTMLInputElement>) {
        switch (e.code) {
            case "Escape":
                return onCancel();
            case "Enter":
                return handleSubmit(e);
            default:
                return;
        }
    }

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        setTextBeingEdited(e.target.value);
    }

    function renderContent() {
        if (isBeingEdited) {
            return (
                <StyledInputContainer
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        maxWidth: maxInputWidth,
                    }}
                >
                    <StyledInput
                        type="text"
                        ref={inputRef}
                        value={textBeingEdited}
                        onKeyUp={handleKeyUp}
                        onChange={handleChange}
                        data-automation="editable-text.input-field"
                        style={{ width: `${textBeingEdited.length}ch`, maxWidth: "100%" }}
                    />
                    <StyledSubmitButton
                        type="flat"
                        iconSize="xs"
                        iconName={iconName}
                        onClick={handleSubmit}
                        isDisabled={!isTextValid(textBeingEdited)}
                        data-automation="editable-text.submit-button"
                    />
                </StyledInputContainer>
            );
        }

        return <StyledText data-automation="editable-text.text">{text}</StyledText>;
    }

    return (
        <div
            className={className}
            data-automation="editable-text"
        >
            {renderContent()}
        </div>
    );
};

export default EditableText;
